import { BooleanFormatter, BooleanRenderer } from '@hr/common/gridcomponents.jsx'
import {
	dateFormatter,
	dateGetter,
	ibanFormatter,
	localized,
	numberFormatter,
	numberFormatterFraction,
} from '@/common/utils.jsx'
import {
	getCompanyName,
	getDimensionValueGetter,
	getNationalityLabel,
	getPaySlipMethods,
	getSupplementColumns,
} from './Utils.js'

export const getColumns = (currentView, params) =>
	currentView === 'external-workforce' ? getExternalWorkforceColumns(params) : getEmployeesColumns(params)

const getPersonStatusColumn = (t) => {
	const translatedStatus = {
		EMPLOYED: t('Employee', 'Työntekijä'),
		NOT_EMPLOYED: t('ExternalParty', 'Ulkopuolinen toimija'),
		ENDED: t('EmploymentEnded', 'Työsuhde päättynyt'),
		ENDED_EXTERNAL: t('EmploymentEnded', 'Työsuhde päättynyt'),
		LONG_TERM_ABSENCE: t('LongTermAbsence', 'Pitkäaikainen poissaolo'),
		LONG_TERM_ABSENCE_EXTERNAL: t('LongTermAbsence', 'Pitkäaikainen poissaolo'),
	}

	return {
		headerName: t('PersonStatus', 'Status'),
		field: 'personStatus',
		hide: true,
		editable: true,
		cellEditor: 'agRichSelectCellEditor',
		cellEditorParams: {
			values: ['EMPLOYED', 'NOT_EMPLOYED'],
			formatValue: (value) => {
				switch (value) {
					case 'NOT_EMPLOYED':
						return t('ExternalParty', 'Ulkopuolinen toimija')
					default:
						return t('Employee', 'Työntekijä')
				}
			},
		},
		valueGetter: (params) => params.data?.employmentDetails?.personStatus,
		cellRenderer: (params) => {
			return (
				<span style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
					<span>{translatedStatus[params.value] || params.value}</span>
					<span style={{ opacity: 0.6 }}>▼</span>
				</span>
			)
		},
		valueSetter: (params) => {
			if (!params.data.employmentDetails) {
				params.data.employmentDetails = {}
			}
			params.data.employmentDetails.personStatus = params.newValue
			return true // value was successfully updated
		},
		filter: 'agTextColumnFilter',
	}
}

const getEmployeesColumns = ({
	t,
	settings,
	tk10,
	cbaCodes,
	countryOptions,
	businessIdToCompanyNameMap,
	supplements,
	genderMap,
	personIdToTeamsMap,
	groupsMap,
}) => {
	const paySlipMethods = getPaySlipMethods(t)

	const endCodeValueGetter = (params) => {
		const endCode = params.data?.employmentDetails?.endCode
		if (endCode && endCode !== 255) return t(`EndCode${endCode}`)
	}

	// Order intentionally follows the legacy Persons view order to prevent regressions
	const columns = [
		{
			headerName: t('Image', 'Kuva'),
			field: 'otherDetails.image',
			filter: false,
			sortable: false,
			suppressSizeToFit: true,
			width: 80,
			cellRenderer: 'profileImage',
			valueFormatter: () => '',
		},
		{
			headerName: t('NumberAbbr', 'Nro'),
			field: 'nr',
			filter: 'agNumberColumnFilter',
			suppressSizeToFit: true,
			minWidth: 115,
			maxWidth: 115,
			width: 115,
		},
		getPersonStatusColumn(t),
		{
			headerName: t('Name', 'Nimi'),
			field: 'name',
			width: 150,
			filterParams: {
				maxNumConditions: 10,
			},
		},
		{
			headerName: t('Surname', 'Sukunimi'),
			field: 'surname',
			hide: true,
		},
		{
			headerName: t('Forename', 'Etunimi'),
			field: 'forename',
			hide: true,
		},
		{
			headerName: t('SocSec', 'Yksilöintitunnus'),
			field: 'socSec',
			hide: true,
		},
		{
			headerName: t('IdentifierCodeType', 'Tunnuksen tyyppi'),
			field: 'gender',
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => genderMap[value],
			hide: true,
		},
		{
			headerName: t('CloseRelatives', 'Lähiomainen'),
			field: 'otherDetails.closeRelatives',
			hide: true,
		},
		{
			headerName: t('Teams', 'Tiimit'),
			colId: 'teams', // Required without field property to remember column state
			valueGetter: (params) => {
				const teams = personIdToTeamsMap[params.data?.personId]
				const resultArray = []
				if (teams?.length) {
					teams.forEach((team) => {
						resultArray.push(team.name)
					})
				}
				return resultArray.join(', ')
			},
			hide: true,
		},
		{
			headerName: t('FixedDuration', 'Määräaikainen'),
			field: 'employmentDetails.fixedDuration',
			valueFormatter: ({ value }) =>
				value ? t('FixedContract', 'Määräaikainen') : t('UntilFurtherNotice', 'Toistaiseksi voimassaoleva'),

			hide: true,
		},
		{
			field: 'employmentDetails.trialPeriod',
			headerName: t('TrialPeriod', 'Sovelletaan koeaikaa'),
			cellRenderer: BooleanRenderer,
			filter: 'agSetColumnFilter',
			filterParams: { valueFormatter: BooleanFormatter },
			hide: true,
		},
		{
			headerName: t('TrialPeriodEndDate', 'Koeaika päättyy'),
			field: 'employmentDetails.trialPeriodEndDate',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			hide: true,
		},
		{
			headerName: t('Place', 'Työntekopaikka/alue'),
			field: 'employmentDetails.place',
			hide: true,
		},
		{
			headerName: t('Language', 'Kieli'),
			field: 'controlDetails.language',
			valueFormatter: ({ value }) => (value ? localized.languageName.of(value) : ''),
			hide: true,
		},
		{
			headerName: t('Phone', 'Puhelin'),
			field: 'details.tel',
			width: 125,
		},
		{
			headerName: `${t('Phone', 'Puhelin')} 2`,
			field: 'details.tel2',
			width: 125,
			hide: true,
		},
		{
			headerName: t('JobTitle', 'Tehtävänimike'),
			field: 'employmentDetails.profession',
		},
		{
			headerName: t('StreetAddress', 'Katuosoite'),
			field: 'details.streetAddress',
			hide: true,
		},
		{
			headerName: t('PostNumber', 'Postinumero'),
			field: 'details.postalCode',
			hide: true,
		},
		{
			headerName: t('Location', 'Paikkakunta'),
			field: 'details.postalAddress',
		},
		{
			headerName: t('Country', 'Maa'),
			field: 'details.country',
			valueFormatter: ({ value }) => (value ? localized.countryName.of(value) : ''),
			hide: true,
		},
		{
			headerName: t('Nationality', 'Kansalaisuus'),
			field: 'nationalDetails.nationality',
			valueGetter: (params) => getNationalityLabel(countryOptions, params.data?.nationalDetails?.nationality),
			hide: true,
		},
		{
			headerName: t('SecondaryNationality', 'Toinen kansalaisuus'),
			field: 'nationalDetails.nationalitySecondary',
			valueGetter: (params) => getNationalityLabel(countryOptions, params.data?.nationalDetails?.nationalitySecondary),
			hide: true,
		},
		{
			headerName: t('IBAN', 'IBAN'),
			field: 'details.iban',
			valueFormatter: ibanFormatter,
		},
		{
			headerName: t('BIC', 'BIC'),
			field: 'details.bic',
		},
		{
			headerName: t('Email', 'Sähköposti'),
			field: 'otherDetails.email',
		},
		{
			headerName: t('EmailHome', 'Sähköposti oma'),
			field: 'otherDetails.email2',
		},
		{
			headerName: t('PayGroup', 'Maksuryhmä'),
			field: 'controlDetails.payGroup',
			hide: true,
		},
		{
			headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
			colId: 'employeeGroup',
			valueGetter: (params) => groupsMap?.[params.data?.controlDetails?.employeeGroupId] ?? '',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: t('TaxNumber', 'Veronumero'),
			field: 'controlDetails.taxNumber',
		},
		{
			headerName: t('Company', 'Yritys'),
			field: 'controlDetails.dimension6',
			valueGetter: (params) => getCompanyName(businessIdToCompanyNameMap, params.data?.controlDetails?.dimension6),
		},
		{
			headerName: t('BusinessId', 'Y-tunnus'),
			field: 'controlDetails.dimension6',
		},
		{
			headerName: t('Supervisor', 'Esihenkilö'),
			field: 'controlDetails.supervisor',
			valueGetter: (params) => params.data?.controlDetails?.supervisor?.personName,
		},
		{
			headerName: `${t('Supervisor', 'Esihenkilö')} 2`,
			field: 'controlDetails.supervisor2',
			valueGetter: (params) => params.data?.controlDetails?.supervisor2?.personName,
		},
		{
			headerName: t('WeeklyWorkHours', 'Viikkotunnit'),
			field: 'salaryDetails.weeklyWorkHours',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatter,
			hide: true,
		},
		{
			headerName: t('StartDate', 'Alkoi'),
			field: 'employmentDetails.startDate',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('NewContractStartDate', 'Uuden työsopimuksen alkupvm.'),
			field: 'employmentDetails.contractStartDate',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			hide: true,
		},
		{
			headerName: t('EndDate', 'Työsuhteen päättymispäivämäärä'),
			field: 'employmentDetails.endDate',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			hide: true,
		},
		{
			headerName: t('EndCode', 'Työsuhteen päättymissyy'),
			field: 'employmentDetails.endCode',
			valueGetter: endCodeValueGetter,
			hide: true,
		},
		{
			headerName: settings.dim1name || t('Dimension1', 'Kustannuspaikka'),
			field: 'settings.dim1',
			valueGetter: getDimensionValueGetter(1, settings),
		},
		{
			headerName: settings.dim2name || t('Dimension2', 'Projekti'),
			field: 'settings.dim2',
			valueGetter: getDimensionValueGetter(2, settings),
		},
		{
			headerName: settings.dim3name || t('Dimension3', 'Dimensio 3'),
			field: 'settings.dim3',
			valueGetter: getDimensionValueGetter(3, settings),
		},
		{
			headerName: settings.dim4name || t('Dimension4', 'Dimensio 4'),
			field: 'settings.dim4',
			valueGetter: getDimensionValueGetter(4, settings),
		},
		{
			headerName: settings.dim5name || t('Dimension5', 'Dimensio 5'),
			field: 'settings.dim5',
			valueGetter: getDimensionValueGetter(5, settings),
		},
		{
			headerName: t('PayslipMethod', 'Laskelman lähetystapa'),
			field: 'controlDetails.payslipMethod',
			valueFormatter: (params) => paySlipMethods[params.value] ?? '',
		},
		{
			headerName: t('FixedTermDuration', 'Määräaikaisuuden kesto'),
			field: 'employmentDetails.fixedDurationEndDate',
			filter: 'agDateColumnFilter',
			valueGetter: (params) => (params.data?.employmentDetails?.fixedDuration ? dateGetter(params) : null),
			valueFormatter: dateFormatter,
			hide: true,
		},
		{
			headerName: t('EmploymentType', 'Palvelussuhteen tyyppi'),
			field: 'salaryDetails.employmentType',
			valueGetter: (params) => {
				const employmentType = params.data?.salaryDetails?.employmentType
				return localized.employmentTypes[employmentType] || employmentType
			},
			hide: true,
		},
		{
			headerName: t('PartTimePercentage', 'Osa-aikaprosentti'),
			field: 'salaryDetails.partTimePercentage',
			filter: 'agNumberColumnFilter',
			valueFormatter: (params) => {
				const val = numberFormatterFraction(params)
				return val ? `${val} %` : val
			},
			hide: true,
		},
		{
			headerName: t('OccupationCodeTK10', 'Ammattinimike (TK10)'),
			field: 'employmentDetails.occupationCodeTK10',
			valueGetter: (params) => {
				const tk10Value = tk10.find(
					(option) => params.data && option.value === params.data.employmentDetails?.occupationCodeTK10,
				)?.label
				return tk10Value?.split(' - ')[1]
			},
			hide: true,
		},
		{
			headerName: t('CBA', 'TES'),
			field: 'employmentDetails.cbaCode',
			valueGetter: (params) => {
				const cbaCodeValue = cbaCodes.find(
					(option) => params.data && option.value === params.data.employmentDetails?.cbaCode,
				)?.label
				return cbaCodeValue?.split(' - ')[1]
			},
		},
		{
			headerName: t('PensionSystem', 'Eläkejärjestelmä'),
			field: 'employmentDetails.pensionSystem',
			hide: true,
		},
		{
			headerName: t('ProfessionCodeAccidentInsurance', 'Tapaturmavakuutuksen ammattikoodi'),
			field: 'employmentDetails.professionCodeAccidentInsurance',
			hide: true,
		},
		{
			headerName: t('SalaryValidFrom', 'Palkkatietojen voimassaolon alkupvm.'),
			field: 'salaryDetails.salaryValidFrom',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			hide: true,
		},
		{
			headerName: t('FormOfSalary', 'Palkkausmuoto'),
			field: 'salaryDetails.formOfSalary',
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => value && value !== '0' && localized.salaryForms[value],
			hide: true,
		},
		{
			headerName: t('MonthlyWage', 'Kuukausipalkka'),
			field: 'salaryDetails.monthlyWage',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
			hide: true,
		},
		{
			headerName: t('HourlyWageATP', 'Aikatuntipalkka'),
			field: 'salaryDetails.hourlyWageATP',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
			hide: true,
		},
		{
			headerName: t('HourlyWagePTP', 'Perustuntipalkka'),
			field: 'salaryDetails.hourlyWagePTP',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
			hide: true,
		},
		{
			headerName: t('HourlyWageOTP', 'Ohjetuntipalkka'),
			field: 'salaryDetails.hourlyWageOTP',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
			hide: true,
		},
		{
			headerName: t('PaymentCoefficient', 'Maksukerroin'),
			field: 'salaryDetails.paymentPercentage',
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
			hide: true,
		},
		{
			headerName: t('ExperienceCode', 'Kokemuskoodi'),
			field: 'salaryDetails.experienceCode',
			hide: true,
		},
		{
			headerName: t('PersonalBenefit1', 'Henk.koht luontoisetu'),
			field: 'salaryDetails.personalBenefit1',
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => value && value !== '0' && localized.personalBenefit[value],
			hide: true,
		},
		{
			headerName: t('PersonalBenefit2', 'Henk.koht luontoisetu 2'),
			field: 'salaryDetails.personalBenefit2',
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => value && value !== '0' && localized.personalBenefit[value],
			hide: true,
		},
		{
			headerName: t('PhoneBenefit', 'Puhelinetu'),
			field: 'salaryDetails.phoneBenefit',
			cellRenderer: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			filterValueGetter: (params) => (params.data.salaryDetails?.phoneBenefit ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			hide: true,
		},
		{
			headerName: t('Keywords', 'Avainsanat'),
			field: 'tags',
			valueGetter: (params) => params.data?.tags?.map((tag) => tag.codeKey),
			tooltipValueGetter: (params) => params.data?.tags?.map((tag) => tag.codeKey),
			hide: true,
		},
		{
			headerName: t('Attachments', 'Liitteet'),
			field: 'otherDetails.attachmentCount',
			valueFormatter: ({ value }) => value || '',
		},
	]

	return [...columns, ...getSupplementColumns(supplements, t)]
}

const getExternalWorkforceColumns = ({ t, businessIdToCompanyNameMap }) => [
	getPersonStatusColumn(t),
	{
		headerName: t('Image', 'Kuva'),
		field: 'otherDetails.image',
		filter: false,
		sortable: false,
		suppressSizeToFit: true,
		width: 80,
		cellRenderer: 'profileImage',
		valueFormatter: () => '',
	},
	{
		headerName: t('NumberAbbr', 'Nro'),
		field: 'nr',
		filter: 'agNumberColumnFilter',
		suppressSizeToFit: true,
		minWidth: 115,
		maxWidth: 115,
		width: 115,
	},
	{
		headerName: t('Name', 'Nimi'),
		field: 'name',
		width: 150,
		filterParams: {
			maxNumConditions: 10,
		},
	},
	{
		headerName: t('Surname', 'Sukunimi'),
		field: 'surname',
		hide: true,
	},
	{
		headerName: t('Forename', 'Etunimi'),
		field: 'forename',
		hide: true,
	},
	{
		headerName: t('Phone', 'Puhelin'),
		field: 'details.tel',
		width: 125,
	},
	{
		headerName: t('Email', 'Sähköposti'),
		field: 'otherDetails.email',
	},
	{
		headerName: t('EmailHome', 'Sähköposti oma'),
		field: 'otherDetails.email2',
	},
	{
		headerName: t('Company', 'Yritys'),
		field: 'controlDetails.dimension6',
		valueGetter: (params) => getCompanyName(businessIdToCompanyNameMap, params.data?.controlDetails?.dimension6),
	},
	{
		headerName: t('BusinessId', 'Y-tunnus'),
		field: 'controlDetails.dimension6',
	},
	{
		headerName: t('Supervisor', 'Esihenkilö'),
		field: 'controlDetails.supervisor',
		valueGetter: (params) => params.data?.controlDetails?.supervisor?.personName,
	},
	{
		headerName: t('Keywords', 'Avainsanat'),
		field: 'tags',
		valueGetter: (params) => params.data?.tags?.map((tag) => tag.codeKey),
		tooltipValueGetter: (params) => params.data?.tags?.map((tag) => tag.codeKey),
	},
	{
		headerName: t('Attachments', 'Liitteet'),
		field: 'otherDetails.attachmentCount',
		valueFormatter: ({ value }) => value || '',
	},
]
